import React, { useState, useEffect, Suspense } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart3, Loader2, Mountain } from 'lucide-react';
import { TrailData } from '@/lib/gpx-loader';

interface ElevationProfileLoaderProps {
  trailData: TrailData;
  hoveredIndex?: number | null;
  onHover?: (point: {lat: number, lon: number, elevation: number, distance: number} | null) => void;
}

/**
 * Lazy loaded ElevationProfile component for chart library optimization
 */
const LazyElevationProfile = React.lazy(() => 
  import('./ElevationProfile').then(module => ({ default: module.default }))
);

/**
 * Chart loading placeholder
 */
const ChartPlaceholder: React.FC<{
  onLoadChart: () => void;
  loading: boolean;
  trailData: TrailData;
}> = ({ onLoadChart, loading, trailData }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Mountain className="w-5 h-5 text-green-600" />
        고도 프로파일
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="h-[200px] flex items-center justify-center bg-gradient-to-br from-blue-50 to-green-50 rounded-lg">
        <div className="text-center space-y-4">
          {loading ? (
            <>
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto" />
              <div>
                <p className="text-sm font-medium text-gray-700">차트 로딩 중...</p>
                <p className="text-xs text-gray-500">고도 프로파일을 불러오는 중입니다.</p>
              </div>
            </>
          ) : (
            <>
              <BarChart3 className="w-8 h-8 text-blue-600 mx-auto" />
              <div>
                <p className="text-sm font-medium text-gray-700">고도 프로파일</p>
                <p className="text-xs text-gray-500">
                  최고 {trailData.stats.maxElevation.toFixed(0)}m, 
                  최저 {trailData.stats.minElevation.toFixed(0)}m
                </p>
                <Button 
                  onClick={onLoadChart} 
                  size="sm"
                  className="mt-2"
                >
                  차트 보기
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

/**
 * Chart loading skeleton
 */
const ChartSkeleton: React.FC = () => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Mountain className="w-5 h-5 text-green-600" />
        고도 프로파일
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="space-y-4">
        <div className="h-[200px] bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg relative overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="space-y-2">
              <Skeleton className="w-12 h-12 rounded mx-auto" />
              <Skeleton className="w-16 h-3" />
            </div>
          </div>
          
          {/* Chart-like decorative elements */}
          <svg className="absolute inset-0 w-full h-full opacity-20">
            <path
              d="M 0 150 L 50 120 L 100 140 L 150 90 L 200 110 L 250 80 L 300 100 L 350 70"
              stroke="#10b981"
              strokeWidth="2"
              fill="none"
            />
          </svg>
        </div>
        
        <div className="flex justify-between text-sm">
          <Skeleton className="w-12 h-4" />
          <Skeleton className="w-16 h-4" />
          <Skeleton className="w-12 h-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

/**
 * ElevationProfile with dynamic chart library loading for bundle optimization
 * 
 * Benefits:
 * - Loads chart.js only when elevation profile is requested
 * - Reduces main bundle size significantly
 * - Provides graceful loading states
 * - Maintains full chart functionality when loaded
 */
export const ElevationProfileLoader: React.FC<ElevationProfileLoaderProps> = (props) => {
  // 자동 로드: 초기값을 true로 설정
  const [shouldLoadChart, setShouldLoadChart] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  // 컴포넌트 마운트 시 자동으로 차트 로드
  useEffect(() => {
    // Small delay to show loading state briefly
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // 이제 차트가 항상 로드되므로 placeholder는 표시되지 않음
  if (!shouldLoadChart || isLoading) {
    return <ChartSkeleton />;
  }

  return (
    <Suspense fallback={<ChartSkeleton />}>
      <LazyElevationProfile {...props} />
    </Suspense>
  );
};